import type { WalletContextState } from '@solana/wallet-adapter-react';
import bs58 from 'bs58';
import type { WalletAdapter } from './upload-providers/types';

interface WalletAdapterFactoryOptions {
  throwOnInvalid?: boolean;
  includeSendTransaction?: boolean;
  includeTransactionSigning?: boolean;
  normalizeSignature?: boolean;
  onSignMessageRequested?: () => void;
}

interface ArDriveWalletAdapterOptions {
  throwOnInvalid?: boolean;
  requireTransactionSigning?: boolean;
}

const defaultFactoryOptions: Required<
  Omit<WalletAdapterFactoryOptions, 'onSignMessageRequested'>
> = {
  throwOnInvalid: true,
  includeSendTransaction: true,
  includeTransactionSigning: false,
  normalizeSignature: false,
};

function handleInvalidWallet(
  message: string,
  throwOnInvalid: boolean,
): WalletAdapter | null {
  if (throwOnInvalid) {
    throw new Error(message);
  }
  return null;
}

export function normalizeSignedMessageResult(
  signature:
    | Uint8Array
    | {
        signature: Uint8Array;
      },
): Uint8Array {
  const bytes =
    typeof signature === 'object' &&
    signature !== null &&
    'signature' in signature
      ? signature.signature
      : signature;

  if (bytes instanceof Uint8Array) {
    return bytes;
  }

  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(bytes)) {
    return new Uint8Array(bytes);
  }

  return new Uint8Array(Buffer.from(bytes));
}

export function getWalletPublicKeyBase58(walletAdapter: WalletAdapter): string {
  const publicKeyToString =
    typeof walletAdapter.publicKey.toString === 'function' &&
    walletAdapter.publicKey.toString !== Object.prototype.toString
      ? walletAdapter.publicKey.toString.bind(walletAdapter.publicKey)
      : undefined;

  return (
    walletAdapter.publicKey.toBase58?.() ??
    publicKeyToString?.() ??
    bs58.encode(walletAdapter.publicKey.toBuffer())
  );
}

export type TurboSolanaSignerMode = 'hex' | 'raw';

export interface TurboSolanaWalletAdapter {
  publicKey: {
    toBuffer: () => Buffer;
    toBase58: () => string;
    toString: () => string;
  };
  signMessage: (message: Uint8Array) => Promise<Uint8Array>;
  signTransaction: NonNullable<WalletAdapter['signTransaction']>;
  signAllTransactions?: WalletAdapter['signAllTransactions'];
  sendTransaction?: WalletAdapter['sendTransaction'];
}

export function createTurboSolanaWalletAdapter(
  walletAdapter: WalletAdapter,
): TurboSolanaWalletAdapter {
  const publicKeyBase58 = getWalletPublicKeyBase58(walletAdapter);

  return {
    publicKey: {
      toBuffer: () => walletAdapter.publicKey.toBuffer(),
      toBase58: () => publicKeyBase58,
      toString: () => publicKeyBase58,
    },
    signMessage: async (message: Uint8Array) =>
      normalizeSignedMessageResult(await walletAdapter.signMessage(message)),
    signTransaction: async (transaction) => {
      if (!walletAdapter.signTransaction) {
        throw new Error(
          'Wallet adapter must support signTransaction for this operation',
        );
      }

      return walletAdapter.signTransaction.call(walletAdapter, transaction);
    },
    signAllTransactions: walletAdapter.signAllTransactions?.bind(walletAdapter),
    sendTransaction: walletAdapter.sendTransaction?.bind(walletAdapter),
  };
}

export function createWalletAdapter(
  wallet: WalletContextState,
  options: WalletAdapterFactoryOptions = {},
): WalletAdapter | null {
  const resolved = { ...defaultFactoryOptions, ...options };

  if (!wallet.publicKey) {
    return handleInvalidWallet('Wallet not connected', resolved.throwOnInvalid);
  }

  if (!wallet.signMessage) {
    return handleInvalidWallet(
      'Wallet does not support message signing',
      resolved.throwOnInvalid,
    );
  }

  const publicKey = wallet.publicKey;
  const signMessage = wallet.signMessage.bind(wallet);

  const adapter: WalletAdapter = {
    publicKey: {
      toBuffer: () => Buffer.from(publicKey.toBytes()),
      toBase58: () => publicKey.toBase58(),
    },
    signMessage: async (message: Uint8Array) => {
      resolved.onSignMessageRequested?.();
      const signature = await signMessage(message);
      return resolved.normalizeSignature
        ? normalizeSignedMessageResult(signature)
        : signature;
    },
  };

  if (resolved.includeSendTransaction && wallet.sendTransaction) {
    adapter.sendTransaction = wallet.sendTransaction.bind(wallet);
  }

  if (resolved.includeTransactionSigning) {
    if (wallet.signTransaction) {
      adapter.signTransaction = wallet.signTransaction.bind(wallet);
    }

    if (wallet.signAllTransactions) {
      adapter.signAllTransactions = wallet.signAllTransactions.bind(wallet);
    }
  }

  return adapter;
}

export function createArDriveWalletAdapter(
  wallet: WalletContextState,
  options: ArDriveWalletAdapterOptions = {},
): WalletAdapter | null {
  const throwOnInvalid = options.throwOnInvalid ?? true;

  if (!wallet.publicKey) {
    return handleInvalidWallet('Wallet not connected', throwOnInvalid);
  }

  if (!wallet.signMessage) {
    return handleInvalidWallet(
      'Wallet does not support message signing',
      throwOnInvalid,
    );
  }

  if (options.requireTransactionSigning && !wallet.signTransaction) {
    return handleInvalidWallet(
      'Wallet does not support transaction signing',
      throwOnInvalid,
    );
  }

  const publicKey = wallet.publicKey;

  return {
    publicKey: {
      toBuffer: () => Buffer.from(publicKey.toBytes()),
      toBase58: () => publicKey.toBase58(),
    },
    signMessage: wallet.signMessage,
    signTransaction: wallet.signTransaction,
    sendTransaction: wallet.sendTransaction,
  };
}
